import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/index.css";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import ActivePlanBadge from "./components/ActivePlanBadge";

import Home from "./pages/Home";
import Listings from "./pages/Listings";
import ListingDetail from "./pages/ListingDetail";
import SearchPage from "./pages/Search";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Pricing from "./pages/Pricing";
import ForgotPassword from "./pages/ForgotPassword";
import About from "./pages/About";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import RefundPolicy from "./pages/RefundPolicy";
import CancellationPolicy from "./pages/CancellationPolicy";
import PaymentPolicy from "./pages/PaymentPolicy";
import Disclaimer from "./pages/Disclaimer";
import SafetyTips from "./pages/SafetyTips";
import Admin from "./pages/Admin";
import BecomeSeller from "./pages/BecomeSeller";
import ProjectDetail from "./pages/ProjectDetail";
import MasterPlan from "./pages/MasterPlan";
import MasterPlanDetail from "./pages/MasterPlanDetail";
import Requirements from "./pages/Requirements";
import ApplyForLoan from "./components/ApplyForLoan";

// Lazy-load dashboard (code splitting)
const DashboardLayout = lazy(() => import("./pages/dashboard/DashboardLayout"));
const Overview = lazy(() => import("./pages/dashboard/Overview"));
const MyListings = lazy(() => import("./pages/dashboard/MyListings"));
const CreateListing = lazy(() => import("./pages/dashboard/CreateListing"));
const EditListing = lazy(() => import("./pages/dashboard/EditListing"));
const Profile = lazy(() => import("./pages/dashboard/Profile"));
const Transactions = lazy(() => import("./pages/dashboard/Transactions"));

const Fallback = () => (
  <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
);

// Renders the floating "Apply for Loan" button only on the homepage.
function HomepageOnlyLoanButton() {
  const location = useLocation();
  if (location.pathname !== "/") return null;
  return <ApplyForLoan />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Fallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/listings" element={<Listings />} />
            <Route path="/listings/:category" element={<Listings />} />
            <Route path="/listings/:id/:slug" element={<ListingDetail />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/master-plan" element={<MasterPlan />} />
            <Route path="/master-plan/:id" element={<MasterPlanDetail />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/cancellation" element={<CancellationPolicy />} />
            <Route path="/payment-policy" element={<PaymentPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/safety-tips" element={<SafetyTips />} />
            <Route path="/requirements" element={<Requirements />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/become-seller" element={<BecomeSeller />} />

            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Overview />} />
              <Route path="my-listings" element={<MyListings />} />
              <Route path="create-listing" element={<CreateListing />} />
              <Route path="edit/:id" element={<EditListing />} />
              <Route path="profile" element={<Profile />} />
              <Route path="transactions" element={<Transactions />} />
            </Route>
          </Routes>
        </Suspense>
        <ActivePlanBadge />
        <HomepageOnlyLoanButton />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
